import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { API_URL, wishlist as wishlistApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export const ProductCard = ({ product, wishlistIds = [] }) => {
  const { user } = useAuth();
  const [imgLoaded, setImgLoaded] = useState(false);
  const [wishlisted, setWishlisted] = useState(wishlistIds.includes(product?.id));

  if (!product) return null;

  const imageUrl = product.images?.[0]
    ? (product.images[0].startsWith('http') ? product.images[0] : `${API_URL}${product.images[0]}`)
    : 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=600';

  const secondImage = product.images?.[1]
    ? (product.images[1].startsWith('http') ? product.images[1] : `${API_URL}${product.images[1]}`)
    : null;

  const linkPath = `/product/${product.id}`;
  const isLowStock = product.stock === 1;

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return toast.error('Login to save items');
    try {
      if (wishlisted) {
        await wishlistApi.remove(product.id);
        setWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await wishlistApi.add(product.id);
        setWishlisted(true);
        toast.success('Saved to wishlist');
      }
    } catch {
      toast.error('Something went wrong');
    }
  };

  return (
    <div className="product-card group" data-testid={`product-card-${product.id}`}>
      <Link to={linkPath} className="block">
        <div className="product-card-image relative">
          {/* Shimmer skeleton */}
          {!imgLoaded && (
            <div className="absolute inset-0 skeleton" data-testid={`skeleton-${product.id}`} />
          )}

          {/* Primary image */}
          <img
            src={imageUrl}
            alt={product.name}
            className={`w-full h-full object-cover transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            loading="lazy"
            width={600}
            height={800}
            onLoad={() => setImgLoaded(true)}
          />

          {/* Second image on hover (desktop) */}
          {secondImage && (
            <img
              src={secondImage}
              alt={`${product.name} alternate`}
              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-400"
              loading="lazy"
              width={600}
              height={800}
            />
          )}

          {/* Only 1 Left badge */}
          {isLowStock && (
            <div className="absolute top-2.5 left-2.5 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1" data-testid={`low-stock-badge-${product.id}`}>
              Only 1 Left
            </div>
          )}

          {/* Wishlist button */}
          <button
            onClick={handleWishlist}
            className="absolute top-2.5 right-2.5 w-8 h-8 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white btn-icon"
            data-testid={`wishlist-btn-${product.id}`}
          >
            <Heart
              className={`w-4 h-4 transition-colors ${wishlisted ? 'fill-red-500 text-red-500' : 'text-[#0A0A0A]'}`}
              strokeWidth={1.5}
            />
          </button>

          {/* Condition badge */}
          {product.condition_grade && (
            <div className="absolute bottom-2.5 left-2.5 bg-white/90 backdrop-blur-sm text-[9px] font-semibold uppercase tracking-wider px-2 py-1 text-[#0A0A0A]" data-testid={`condition-badge-${product.id}`}>
              {product.condition_grade}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="pt-3 pb-1">
          {product.brand && product.brand !== 'Unknown Brand' && product.brand !== 'Unknown' && (
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#666] mb-0.5" data-testid={`product-brand-${product.id}`}>
              {product.brand}
            </p>
          )}
          <h3 className="text-sm font-medium text-[#0A0A0A] leading-tight mb-1 line-clamp-1" data-testid={`product-name-${product.id}`}>
            {product.name}
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#0A0A0A]" data-testid={`product-price-${product.id}`}>
              &pound;{product.price?.toFixed(2)}
            </span>
            {product.size && (
              <span className="text-[10px] text-[#999] uppercase">{product.size}</span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};
